import { ICON_CS } from '@repo/assets';
import { Button, Icon } from '@successmode-ktp/kui';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { ChatBox } from './ChatBox';
import { SendModal } from './Send.modal';
import { QNA, QNA_MODIFY_EMAIL_SUCCESS_MODAL } from '@repo/constants';
import { AlertModal, TimeLimit } from '@repo/components';
import { getKorDate, isBusinessTime } from '@repo/utils';
import { ModifyEmailModal } from './ModifyEmail.modal';

type Chat = {
  questionId: number;
  content: string;
  createdDate?: string;
  createdAt?: string;
  isQuestion: boolean;
  imageUrls: string[];
};

type Props = {
  passportNumber: string;
  nationality: string;
  email: string;
  onClose: () => void;
  onSendMessage: (v: FormData, onSuccess?: () => void) => void;
  onModifyEmail: (email: string) => void;
  qnaList?: Chat[];
  onGetQnaList: () => void;
  lang: 'KOR' | 'ENG';
  isKiosk?: boolean;
  isBill?: boolean;
  isLoading?: boolean;
  isModifyEmailSuccess: boolean;
};

export const Chat = ({
  passportNumber,
  nationality,
  email,
  onClose,
  onSendMessage,
  onModifyEmail,
  qnaList,
  onGetQnaList,
  lang,
  isKiosk = false,
  isBill = false,
  isLoading = false,
  isModifyEmailSuccess,
}: Props) => {
  const [isOpen, setIsOpen] = useState({
    send: false,
    modifyEmail: false,
    modifyEmailSuccess: false,
  });

  useEffect(() => {
    onGetQnaList && onGetQnaList();
  }, []);

  const handleModifyEmail = (email: string) => {
    onModifyEmail(email);
  };

  useEffect(() => {
    if (isModifyEmailSuccess) {
      setIsOpen({ ...isOpen, modifyEmailSuccess: true });
    }
  }, [isModifyEmailSuccess]);

  return (
    <>
      <HeaderWrapper>
        <TitleWrapper>
          <Title>
            <IconCs src={ICON_CS} alt='' />
            {QNA[lang].title}
          </Title>
          <button type='button' onClick={onClose}>
            <Icon.X fillColor='#A7A7A7' />
          </button>
        </TitleWrapper>
        <InfosWrapper>
          <InfoWrapper $nationality={nationality}>
            <InfoContent>
              <dt>{QNA[lang].passport}</dt>
              <dd>{passportNumber}</dd>
            </InfoContent>
          </InfoWrapper>
          <InfoWrapper $nationality={nationality}>
            <InfoContent>
              <dt>{QNA[lang].email}</dt>
              <dd>{email}</dd>
            </InfoContent>
            <ChangeEmailButton
              onClick={() => setIsOpen({ ...isOpen, modifyEmail: true })}
            >
              {QNA[lang].modifyEmail}
            </ChangeEmailButton>
          </InfoWrapper>
        </InfosWrapper>
      </HeaderWrapper>
      <DivideLine />
      {!isBusinessTime(getKorDate()) && <TimeLimit lang={lang} />}
      <ChatWrapper $isTimeOver={!isBusinessTime(getKorDate())}>
        {qnaList?.length === 0 && (
          <EmptyWrapper>
            <Icon.EmptyDocument fillColor='#BBB' />
            <EmptyText>{QNA[lang].empty}</EmptyText>
          </EmptyWrapper>
        )}
        {qnaList?.length !== 0 &&
          qnaList?.map((chat) => {
            const createdDate = chat.createdDate || chat.createdAt || '';
            return (
              <ChatContainer
                $isQuestion={chat.isQuestion}
                key={chat.questionId}
              >
                <ChatBox
                  content={chat.content}
                  createdDate={createdDate}
                  isQuestion={chat.isQuestion}
                  imageUrls={chat.imageUrls}
                  onImageExpired={onGetQnaList}
                />
              </ChatContainer>
            );
          })}
      </ChatWrapper>
      <ButtonWrapper>
        <Button
          type='button'
          style={{ gap: '8px' }}
          onClick={() => setIsOpen({ ...isOpen, send: true })}
        >
          <Icon.Edit fillColor='#fff' />
          {QNA[lang].btnText}
        </Button>
      </ButtonWrapper>
      {isOpen && (
        <SendModal
          isOpen={isOpen.send}
          onClose={() => setIsOpen({ ...isOpen, send: false })}
          onSubmit={onSendMessage}
          passportNumber={passportNumber}
          isLoading={isLoading}
          lang={lang}
          isKiosk={isKiosk}
          isBill={isBill}
        />
      )}
      {isOpen.modifyEmail && (
        <ModifyEmailModal
          isOpen={isOpen.modifyEmail}
          onClose={() => setIsOpen({ ...isOpen, modifyEmail: false })}
          lang={lang}
          email={email}
          onModify={handleModifyEmail}
        />
      )}
      <AlertModal
        isOpen={isOpen.modifyEmailSuccess}
        onClick={() => setIsOpen({ ...isOpen, modifyEmailSuccess: false })}
        title={QNA_MODIFY_EMAIL_SUCCESS_MODAL[lang].title}
        description={QNA_MODIFY_EMAIL_SUCCESS_MODAL[lang].description}
        button={QNA_MODIFY_EMAIL_SUCCESS_MODAL[lang].button}
      />
    </>
  );
};

const TitleWrapper = styled.h6`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #030303;
  font-size: 20px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.4px;
`;

const IconCs = styled.img`
  display: block;
  width: 24px;
  height: 24px;
`;

const InfosWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
`;

const InfoWrapper = styled.dl<{ $nationality: string }>`
  display: flex;
  border-radius: 8px;
  background: #f5f6f7;
  padding: 12px;
  width: 100%;
  justify-content: space-between;
  dt {
    color: #777;
    font-size: 13px;
    font-weight: 500;
    line-height: 150%;
    letter-spacing: -0.26px;
    width: ${({ $nationality }) => ($nationality === 'KOR' ? '45px' : '74px')};
    white-space: nowrap;
  }
  dd {
    color: #111;
    font-size: 13px;
    font-weight: 500;
    line-height: 150%;
    letter-spacing: -0.26px;
  }
`;

const InfoContent = styled.div`
  flex: 1;
  display: flex;
  gap: 16px;
`;

const ChangeEmailButton = styled.button`
  color: #999;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.26px;
  text-decoration-line: underline;
  align-self: end;
  width: fit-content;
`;

const HeaderWrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const DivideLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: #f4f4f4;
`;

const ChatWrapper = styled.div<{ $isTimeOver: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
  height: calc(100vh - 280px);
  padding: ${({ $isTimeOver }) =>
    $isTimeOver ? '60px 20px 110px' : '20px 20px 110px'};
  overflow: auto;
  width: 100%;
`;

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const EmptyText = styled.p`
  color: #bbb;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.26px;
`;

const ChatContainer = styled.div<{ $isQuestion: boolean }>`
  display: flex;
  justify-content: ${({ $isQuestion }) =>
    $isQuestion ? 'flex-end' : 'flex-start'};
`;

const ButtonWrapper = styled.div`
  padding: 20px 20px 40px;
  width: 100%;
  position: fixed;
  bottom: 0;
  background: #fff;
  box-shadow: 0px -3px 6px 0px rgba(203, 203, 203, 0.25);
`;
